import { useEffect, useRef, useState } from "react";
import { BookOpen } from "lucide-react";
import { BandLogEntryForm } from "../../baende/components/BandLogEntryForm";

import type { ClassWeekCacheKey, KioskClassWeekCell } from "../types/kioskApiTypes";
import { useKioskDiarySave } from "../hooks/useKioskDiarySave";
import { AutogrowTextarea } from "./AutogrowTextarea";
import { CellAttendance } from "./CellAttendance";

interface WeekCellProps {
  cell: KioskClassWeekCell;
  userId: string;
  classWeekKey: ClassWeekCacheKey;
  targetLessonId?: string;
  /** Calendar exception covering this lesson's day. When set, the cell
   *  is treated like a class-absence cancellation — diary + attendance
   *  inputs are hidden, the cell shows an "Entfällt — {label}" badge,
   *  and clicking does nothing. */
  dayException?: { label: string } | null;
}

export function WeekCell({
  cell,
  userId,
  classWeekKey,
  targetLessonId,
  dayException,
}: WeekCellProps) {
  const isTarget = targetLessonId === cell.lesson_id;
  const canEdit = cell.is_mine && !dayException;
  const [inhalt, setInhalt] = useState(cell.diary_text ?? "");
  const [hausaufgabe, setHausaufgabe] = useState(cell.diary_homework ?? "");
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  // Edit mode for non-current lessons — toggled by clicking the cell.
  const [localEditing, setLocalEditing] = useState(false);
  const targetAutoOpenedRef = useRef(false);

  const { trySave, isSaving, savedFlash, saveError } = useKioskDiarySave(cell, classWeekKey, userId);

  // Re-tick once a minute so the "current lesson" highlight tracks the clock
  // (and the "noch nicht begonnen" gate flips when the lesson starts).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const now = Date.now();
  // 10-min tolerance window — matches fn_kiosk_chip_login's current_lesson
  // detection so the JETZT highlight and the "can record attendance" gate
  // both kick in slightly before and after the official lesson slot.
  const TOLERANCE_MS = 10 * 60_000;
  const startMs = new Date(cell.start_at).getTime();
  const endMs   = new Date(cell.end_at).getTime();
  const isCurrent = startMs - TOLERANCE_MS <= now && now < endMs + TOLERANCE_MS;
  // Suppress unused-variable warning on tick (it just forces re-render).
  void tick;

  // Auto-scroll into view: either the current lesson (JETZT) or a lesson that
  // was explicitly targeted from the open-entries list.
  const cellRef = useRef<HTMLDivElement | null>(null);
  const wasCurrentRef = useRef(false);
  useEffect(() => {
    if (isCurrent && !wasCurrentRef.current && cellRef.current) {
      cellRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    wasCurrentRef.current = isCurrent;
  }, [isCurrent]);
  useEffect(() => {
    if (isTarget && cellRef.current) {
      cellRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Only run on mount — targetLessonId is stable for the lifetime of this view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isTarget || !canEdit || isCurrent || targetAutoOpenedRef.current) return;
    setLocalEditing(true);
    targetAutoOpenedRef.current = true;
  }, [isTarget, canEdit, isCurrent]);

  // Reflect upstream refetches: if the row reloads with different content
  // (e.g. another teacher edited a substitution row), reset local state —
  // but only when the user isn't actively typing.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current) return;
    setInhalt(cell.diary_text ?? "");
    setHausaufgabe(cell.diary_homework ?? "");
  }, [cell.diary_text, cell.diary_homework]);

  // Show inputs when: it's the teacher's lesson AND (it's happening now OR they clicked the cell).
  const editing = canEdit && (isCurrent || localEditing);

  const tint = cell.is_adhoc
    ? // Adhoc lessons (e.g. an Ausflug) — amber so the trip leads the day.
      "bg-amber-100/70 ring-1 ring-inset ring-amber-300/70"
    : !canEdit
      ? cell.is_filled
        ? "bg-slate-50"
        : "bg-white"
      : cell.is_filled || inhalt.trim()
        ? "bg-emerald-50/40"
        : "bg-amber-50/40 ring-1 ring-inset ring-amber-200/60";

  // A targeted lesson (opened from the fehlende-Stunden list) gets an amber
  // ring. The "current lesson" ring is drawn by ClassWeek around the whole
  // period cell (so it encloses the concurrent course squares too).
  const currentEmphasis = isTarget
    ? "relative z-10 ring-2 ring-amber-400 shadow-md shadow-amber-400/15 !bg-amber-50/60"
    : "";

  const handleCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canEdit || isCurrent) return;
    const target = e.target as HTMLElement;
    if (target.closest("input,textarea,button")) return;
    setLocalEditing((v) => !v);
  };

  return (
    <div
      ref={cellRef}
      onClick={handleCellClick}
      className={`flex flex-col px-1.5 py-1 border-b last:border-b-0 border-slate-200 transition-colors ${canEdit && !isCurrent ? "cursor-pointer hover:bg-slate-50/80" : ""} ${tint} ${currentEmphasis}`}
    >
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <div className="flex items-center gap-1 min-w-0">
          {/* Pflicht (curriculum) subjects → red, others → blue. */}
          <span
            className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
              cell.is_curriculum_subject ? "bg-red-500" : "bg-blue-500"
            }`}
            title={
              cell.is_curriculum_subject ? "Pflichtfach" : "kein Pflichtfach"
            }
          />
          <span
            className={`font-semibold text-sm leading-tight truncate ${
              dayException ? "text-slate-400 line-through" : "text-slate-900"
            }`}
            title={cell.subject_name ?? cell.subject_abbreviation ?? undefined}
          >
            {(() => {
              const s = cell.subject_abbreviation ?? cell.subject_name ?? "—";
              // Adhoc lesson names (e.g. "ausflug – …") are free text —
              // capitalise the first letter for display.
              return cell.is_adhoc && s
                ? s.charAt(0).toUpperCase() + s.slice(1)
                : s;
            })()}
          </span>
          {isCurrent ? (
            <span className="text-[9px] uppercase tracking-wider font-bold text-white bg-sky-500 px-1 rounded animate-pulse">
              jetzt
            </span>
          ) : null}
          {cell.is_substitution ? (
            <span className="text-[9px] text-sky-700 italic">
              {cell.substitution_type ?? "Vertr."}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          {savedFlash ? (
            <span className="text-[10px] text-emerald-700">✓</span>
          ) : isSaving ? (
            <span className="text-[10px] text-stone-500">…</span>
          ) : null}
          {cell.room_number ? (
            <span
              className="text-[10px] text-slate-500"
              title={cell.room_name ?? undefined}
            >
              {cell.room_number}
            </span>
          ) : null}
          {cell.band_id ? (
            <span
              className="flex-shrink-0 leading-none"
              title={
                cell.band_name
                  ? cell.band_done
                    ? `${cell.band_name} — durchgeführt`
                    : cell.band_name
                  : "Band"
              }
            >
              <BookOpen
                className="size-3.5 text-teal-600"
                fill={cell.band_done ? "currentColor" : "none"}
              />
            </span>
          ) : null}
          {cell.teachers && cell.teachers.length > 0 ? (
            <span className="flex items-center gap-1 flex-wrap justify-end">
              {cell.teachers.map((t) => (
                <span
                  key={t.id}
                  title={t.name ?? undefined}
                  className={`text-[10px] ${
                    t.is_primary
                      ? "text-slate-600 font-medium"
                      : "text-slate-400"
                  }`}
                >
                  {t.kuerzel ?? t.name ?? "?"}
                </span>
              ))}
            </span>
          ) : cell.primary_teacher_initials ? (
            <span className="text-[10px] text-slate-500">
              {cell.primary_teacher_initials}
            </span>
          ) : null}
          {canEdit && !isCurrent ? (
            <span
              className="text-[11px] text-slate-400 leading-none"
              title={localEditing ? "Klick in die Kachel, um Bearbeitung zu schließen" : "Klick in die Kachel, um zu bearbeiten"}
            >
              {localEditing ? "✕" : "✎"}
            </span>
          ) : null}
          {/* Attendance status — green check when recorded, red cross
              when still outstanding. Suppressed on holiday days: no
              attendance is expected on a school-closed day. */}
          {!dayException && (
            <span
              className={`text-[12px] leading-none font-bold ${
                cell.attendance_recorded ? "text-emerald-600" : "text-rose-500"
              }`}
              title={
                cell.attendance_recorded
                  ? "Anwesenheit erfasst"
                  : "Anwesenheit fehlt"
              }
            >
              {cell.attendance_recorded ? "✓" : "✗"}
            </span>
          )}
        </div>
      </div>

      {dayException ? (
        // Holiday / school-closed: column header already says "geschlossen"
        // — repeating "ENTFÄLLT — {label}" on every cell is noisy. Just
        // strike the subject (done above) and leave the body empty. Click
        // / diary / attendance inputs are already disabled.
        null
      ) : cell.class_absence_label ? (
        <div className="flex-1 flex">
          <div className="w-full rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[12px] leading-snug">
            <span className="font-bold text-amber-700 uppercase tracking-wide">
              Entfällt
            </span>
            <span className="text-amber-800"> — {cell.class_absence_label}</span>
          </div>
        </div>
      ) : editing ? (
        <div className="flex-1 flex flex-col gap-0.5">
          <AutogrowTextarea
            value={inhalt}
            onChange={setInhalt}
            onFocus={() => { focusedRef.current = true; }}
            onBlur={() => {
              focusedRef.current = false;
              trySave(inhalt, hausaufgabe);
            }}
            placeholder="Inhalt der Stunde…"
            className="w-full bg-white border border-slate-200 text-[12px] text-slate-800 placeholder:text-slate-400 resize-none focus:outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-100 rounded-md px-1.5 py-0.5 leading-snug"
            minRows={2}
          />
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-500">HA</span>
            <input
              value={hausaufgabe}
              onChange={(e) => setHausaufgabe(e.target.value)}
              onFocus={() => { focusedRef.current = true; }}
              onBlur={() => {
                focusedRef.current = false;
                trySave(inhalt, hausaufgabe);
              }}
              placeholder="Hausaufgabe…"
              className="flex-1 bg-white border border-slate-200 text-[11px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-100 rounded-md px-1.5 py-0.5"
            />
          </div>
          <CellAttendance
            cell={cell}
            userId={userId}
            classWeekKey={classWeekKey}
            open={attendanceOpen}
            onOpen={() => setAttendanceOpen(true)}
            onClose={() => setAttendanceOpen(false)}
          />
          {/* Leseband (or other band) eintrag — same form as the
              Stundenplan; loads its own data + saves itself. */}
          {cell.band_id ? (
            <BandLogEntryForm
              lessonId={cell.lesson_id}
              classId={classWeekKey.classId}
              dateStr={cell.start_at}
              periodNumber={cell.period_number}
            />
          ) : null}
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          {cell.diary_text ? (
            <div className="flex-1 text-[12px] text-slate-700 leading-snug whitespace-pre-wrap line-clamp-2">
              {cell.diary_text}
            </div>
          ) : (
            <div className="flex-1 text-[11px] text-slate-400 italic">—</div>
          )}
          {cell.diary_homework ? (
            <div className="mt-1 text-[11px] text-slate-600">
              <span className="text-slate-500">HA</span> {cell.diary_homework}
            </div>
          ) : null}
        </div>
      )}

      {saveError ? (
        <div className="text-[10px] text-rose-700 mt-1">⚠ {saveError}</div>
      ) : null}
    </div>
  );
}
